import { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useSearchCorrections } from '../useSearchCorrections';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback';
import {
    trackEcommerceProductClick,
    trackEcommerceProductImpressions,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';

import { SbSharedItemTrackingProvider_viewer$key } from './__generated__/SbSharedItemTrackingProvider_viewer.graphql';
import { SbSharedItemTrackingProvider_itemSearch$key } from './__generated__/SbSharedItemTrackingProvider_itemSearch.graphql';
import { SbSharedItemTrackingProvider_item$key } from './__generated__/SbSharedItemTrackingProvider_item.graphql';

const viewerFragment = graphql`
    fragment SbSharedItemTrackingProvider_viewer on Viewer {
        regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
            @include(if: $fetchRegionalInfo) {
            regionsByZipCode {
                displayName
            }
        }
    }
`;

const itemSearchFragment = graphql`
    fragment SbSharedItemTrackingProvider_itemSearch on ItemSearchQueryConnection {
        ...ecommerceTracking_itemSearch
        ...useSearchCorrections_itemSearch
    }
`;

const itemFragment = graphql`
    fragment SbSharedItemTrackingProvider_item on Item @relay(plural: true) {
        ...ecommerceTracking_item
    }
`;

type ItemData = {
    itemId: string | null;
    index: number;
    isSponsored?: boolean;
};

export type TrackingFunction = (props: ItemData) => void;

type Props = {
    viewer: SbSharedItemTrackingProvider_viewer$key;
    itemSearch: SbSharedItemTrackingProvider_itemSearch$key;
    items: SbSharedItemTrackingProvider_item$key;
    children: (props: {
        fireItemImpressionTracking: TrackingFunction;
        fireItemClickTracking: TrackingFunction;
    }) => ReactElement;
    isRelatedSearchItems?: boolean;
    pageType?: string;
};

const dedupeItem = ({ itemId, index }: ItemData): string => `${index}_${itemId}`;

export const SbSharedItemTrackingProvider: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    items: itemsRef,
    children,
    isRelatedSearchItems,
    pageType,
}) => {
    const [trackedItemImpressionItems, setTrackedItemImpressionItems] = useState<string[]>([]);
    const [attemptedItemImpressionItems, setAttemptedItemImpressionItems] = useState<ItemData[]>(
        []
    );

    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const items = useFragment(itemFragment, itemsRef);
    const [searchCorrectionTerm] = useSearchCorrections({ itemSearch, includeTracking: false });

    const regionalInfoAvailable = useCallback(() => {
        return !!(getUserSessionRegions() || viewer?.regionalInfo);
    }, [viewer]);

    const getRegions = useCallback(() => {
        return getUserSessionRegions() || viewer?.regionalInfo?.[0]?.regionsByZipCode || [];
    }, [viewer]);

    const trackImpressions = useCallback(
        (itemsData: ItemData[]): void => {
            const userRegions = getRegions();

            trackEcommerceProductImpressions({
                items,
                itemSearch,
                itemsData,
                searchCorrectionTerm,
                userRegions,
                isRelatedSearchItems,
                pageType,
            });
            setTrackedItemImpressionItems(curr => [...curr, ...itemsData.map(dedupeItem)]);
            setAttemptedItemImpressionItems([]);
        },
        [
            getRegions,
            isRelatedSearchItems,
            items,
            itemSearch,
            pageType,
            searchCorrectionTerm,
            setAttemptedItemImpressionItems,
            setTrackedItemImpressionItems,
        ]
    );

    const [debouncedTracking] = useDebouncedCallback(trackImpressions, 200);

    useEffect(() => {
        if (attemptedItemImpressionItems.length > 0 && regionalInfoAvailable()) {
            debouncedTracking(attemptedItemImpressionItems);
        }
    }, [attemptedItemImpressionItems, debouncedTracking, regionalInfoAvailable]);

    const fireItemImpressionTracking: TrackingFunction = useCallback(
        item => {
            const dedupedItem = dedupeItem(item);
            //Checking if item impression is already fired or it is already in attemptedItemImpressionItems list
            if (
                !trackedItemImpressionItems.includes(dedupedItem) &&
                !attemptedItemImpressionItems
                    .map(attemptedItem => dedupeItem(attemptedItem))
                    .includes(dedupedItem)
            ) {
                setAttemptedItemImpressionItems(curr => [...curr, item]);
            }
        },
        [attemptedItemImpressionItems, trackedItemImpressionItems]
    );

    const fireItemClickTracking: TrackingFunction = useCallback(
        ({ itemId, index, isSponsored }) => {
            const userRegions = getRegions();
            //In case clicking on item while impression tracking haven't been done
            if (!trackedItemImpressionItems.includes(dedupeItem({ itemId, index }))) {
                trackImpressions([{ itemId, index, isSponsored }]);
                setAttemptedItemImpressionItems([]);
            }

            trackEcommerceProductClick({
                itemSearch,
                items,
                index,
                searchCorrectionTerm,
                isSponsored,
                pageType,
                userRegions,
            });
        },
        [
            itemSearch,
            items,
            getRegions,
            pageType,
            searchCorrectionTerm,
            trackedItemImpressionItems,
            trackImpressions,
        ]
    );

    return children({ fireItemClickTracking, fireItemImpressionTracking });
};
